import { useAutoneTranslation } from '@autone/translations';
import { useDebounceEffect } from '@autone/utils';
import { motion } from 'motion/react';
import {
  forwardRef,
  type InputHTMLAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  InputWithIcon,
  type InputWithIconProps,
} from '../../atoms/inputs/InputWithIcon';
import { SearchIcon } from '../../icons';
import { cn } from '../../utils';

export interface ExpandableSearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange'> {
  id: string;
  className?: string;
  expandedWidth?: string | number;
  placeholder?: string;
  value: string;
  onChange?: (value: string) => void;
}

export const ExpandableSearchInput = forwardRef<
  HTMLInputElement,
  ExpandableSearchInputProps
>(
  (
    {
      id,
      className,
      expandedWidth = '16rem',
      placeholder,
      onChange,
      value,
      onBlur,
      ...props
    },
    ref,
  ) => {
    const [expanded, setExpanded] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      setExpanded(value.length > 0);
    }, [value.length]);

    useImperativeHandle(ref, () => inputRef.current!, []);

    const handleExpand = () => {
      setExpanded(true);
      setTimeout(() => inputRef.current?.focus(), 250);
    };

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (e.target.value.length === 0) setExpanded(false);
        onBlur?.(e);
      },
      [setExpanded, onBlur],
    );

    const isExpanded = useMemo(
      () => expanded || value.length > 0,
      [expanded, value.length],
    );

    return (
      <div
        className={cn(
          'h-12',
          !isExpanded && '!cursor-pointer hover:bg-secondary',
          className,
        )}
        data-testid={id}
      >
        <motion.div
          className="box-border overflow-hidden focus-within:ring-1 focus-within:ring-ring focus-within:ring-offset-0 rounded-sm"
          onClick={!isExpanded ? handleExpand : undefined}
          initial={false}
          animate={{
            width: isExpanded ? expandedWidth : '3rem',
          }}
          transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20,
            restDelta: 2,
          }}
        >
          <SearchInput
            ref={inputRef}
            id={id}
            variant="default"
            onBlur={handleBlur}
            placeholder={!isExpanded ? '' : placeholder}
            readOnly={!isExpanded}
            value={value}
            debounceDelay={500}
            inputClassName={cn(
              !isExpanded && 'cursor-pointer p-0 caret-transparent',
              'focus:outline-none focus-visible:ring-0',
            )}
            iconClassName={cn('left-4')}
            className="w-full"
            {...props}
            onChange={onChange}
          />
        </motion.div>
      </div>
    );
  },
);

export interface SearchInputProps
  extends Omit<InputWithIconProps, 'size' | 'onChange' | 'icon'> {
  id: string;
  placeholder?: string;
  debounceDelay?: number;
  value?: string;
  onChange?: (value: string) => void;
}
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    { id, placeholder, debounceDelay = 0, onChange, value = '', ...props },
    ref,
  ) => {
    const { common } = useAutoneTranslation();
    const [localValue, setLocalValue] = useState<string>(value);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const searchPlaceholder =
      typeof placeholder === 'string'
        ? placeholder
        : common('search-placeholder');

    const debouncedCallback = useCallback(() => {
      onChange?.(localValue);
    }, [localValue, onChange]);

    useDebounceEffect(debouncedCallback, [localValue], debounceDelay);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalValue(e.target.value);
    };

    useImperativeHandle(ref, () => inputRef.current!, []);

    return (
      <InputWithIcon
        ref={inputRef}
        id={id}
        variant="default"
        placeholder={searchPlaceholder}
        icon={<SearchIcon />}
        className="w-full"
        value={localValue}
        {...props}
        onChange={handleChange}
      />
    );
  },
);

ExpandableSearchInput.displayName = 'SearchInput';
